import { ArrowRightIcon } from 'lucide-react';
import { FC, useMemo } from 'react';
import type { RunStatus, RunTaskDetail, RunTasksCount } from '@/entities/jobs';
import { isRunActive, useRun } from '@/entities/jobs';
import { FallbackCircles } from './FallbackCircles';
import { RunResultCircle } from './RunResultCircle';
import { TaskCircle } from './TaskCircle';

type TaskStatusCirclesProps = {
  runId: string;
  status: RunStatus;
  tasksCount: RunTasksCount;
  onTaskClick?: (runId: string, task: RunTaskDetail) => void;
};

// Вычисляет итоговый статус пайплайна по реальным статусам тасков
function deriveResultStatus(tasks: RunTaskDetail[], fallback: RunStatus): RunStatus {
  if (!tasks.length) return fallback;
  if (tasks.some((t) => t.status === 'FAILED')) return 'FAILED';
  if (tasks.some((t) => t.status === 'RUNNING')) return 'RUNNING';
  return fallback;
}

export const TaskStatusCircles: FC<TaskStatusCirclesProps> = ({ runId, status, tasksCount, onTaskClick }) => {
  // В очереди и до захвата слота у запуска ещё нет задач — детали запрашивать не о чем
  const isPending = status === 'QUEUED' || status === 'PENDING';
  const isActive = isRunActive(status);
  const { data: run } = useRun(runId, { enabled: !isPending, retry: 0, refetchInterval: isActive ? 3000 : false });

  const hasTasks = run?.tasks && run.tasks.length > 0;
  const sortedTasks = useMemo(
    () => (hasTasks ? [...run.tasks].sort((a, b) => a.orderIndex - b.orderIndex) : []),
    [hasTasks, run?.tasks],
  );

  const handleCircleClick = useMemo(() => {
    if (!onTaskClick) return undefined;
    return (task: RunTaskDetail) => onTaskClick(runId, task);
  }, [onTaskClick, runId]);

  const resultStatus = useMemo(() => deriveResultStatus(sortedTasks, status), [sortedTasks, status]);

  return (
    <div className="flex gap-1 items-center py-1">
      {hasTasks ? (
        sortedTasks.map((task) => <TaskCircle key={task.orderIndex} task={task} onClick={handleCircleClick} />)
      ) : (
        <FallbackCircles tasksCount={tasksCount} status={status} />
      )}
      {!isPending && (
        <>
          <ArrowRightIcon className="size-3.5 shrink-0 text-muted-foreground mx-0.5" />
          <RunResultCircle status={resultStatus} />
        </>
      )}
    </div>
  );
};
